<template>
  <div
    class="min-h-screen flex flex-col items-center justify-center py-6 px-4 sm:px-6 lg:px-8 bg-lightGray bg-cover bg-center relative overflow-hidden"
  >
    <!-- <HyperspeedBackground class="absolute inset-0 z-0" :beamCount="120" colorScheme="rainbow" :speedFactor="1.5" /> -->
    <div
      class="max-w-xl w-full space-y-8 bg-white/80 backdrop-blur-xl p-8 rounded-2xl overflow-hidden shadow-2xl shadow-cosmicPurple/10 relative z-10"
    >
      <ShineBorder 
        :border-width="2" 
        :duration="24" 
        :shine-color="['#A07CFE', '#FE8FB5', '#FFBE7B']" 
      />
      <div class="relative flex items-center justify-center">
        <div class="absolute -top-[25rem] z-10 bg-[#4d7aff] w-[30rem] h-[30rem] rounded-full blur-[100px] opacity-30 appear"></div>
        <LogoMainLink class="z-20" />

      </div>
 
      <!-- <h2 class="mt-6 text-center text-3xl text-gray-900 text-pretty font-light">
        Welcome to the launch pad of your career 🚀
      </h2> -->

      <h1 class="text-center text-2xl text-gray-900 text-pretty font-light">
        Sign in to your account
      </h1>

      <Form
        @submit="login"
        class="mt-8 space-y-6 max-w-[20rem] mx-auto"
        :initial-values="credentials"
      >
        <div class="space-y-4">
          <FormField v-slot="{ componentField }" name="email">
            <FormItem>
              <FormControl>
                <Input
                  v-bind="componentField"
                  id="email"
                  type="text"
                  placeholder="Enter your email or username"
                  v-model="credentials.email"
                  required
                />
              </FormControl>
            </FormItem>
          </FormField>

          <FormField v-slot="{ componentField }" name="password">
            <FormItem>
              <FormControl>
                <Input
                  v-bind="componentField"
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  v-model="credentials.password"
                  required
                />
              </FormControl>
            </FormItem>
          </FormField>
        </div>

        <!-- Forgot password link -->
        <div class="flex items-center justify-end">
          <div class="text-sm">
            <router-link
              to="/forgot-password"
              class="font-light text-indigo-600 hover:text-indigo-500"
            >
              Forgot password?
            </router-link>
          </div>
        </div>

        <!-- Display error message -->
        <div v-if="authStore.error" class="text-red-500 text-sm mt-2">
          {{ authStore.error }}
        </div>

        <!-- Sign-in button -->
        <div>
          <Button
            type="submit"
            :disabled="authStore.isLoading"
            class="w-full bg-cosmicPurple shadow-lg shadow-cosmicPurple/20 text-white hover:bg-cosmicPurple/90 hover:-translate-y-[1px] transition-all duration-200"
          >
            <span v-if="!authStore.isLoading">Sign in</span>
            <span v-else>Signing in...</span>
          </Button>
        </div>

        <!-- Social Login Divider -->
        <div class="relative">
          <div class="absolute inset-0 flex items-center">
            <div class="w-full border-t border-gray-300"></div>
          </div>
          <div class="relative flex justify-center text-sm">
            <span class="px-2 bg-white text-gray-500">Or continue with</span>
          </div>
        </div>

        <!-- Social Login Buttons -->
        <SocialLoginButtons />

        <!-- Divider and Register link -->
        <div class="flex items-center justify-center mt-6">
          <div class="text-center text-sm text-gray-600 mt-4">
            Don't have an account?
            <router-link
              to="/register"
              class="font-medium text-indigo-600 hover:text-indigo-500 justify-center"
            >
              Create account
            </router-link>
          </div>
        </div>
      </Form>
    </div>
    <!-- Terms and Conditions -->
    <TermsAndConditionsLinks />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useUserStore } from '@/stores/user'
import { consumePostLoginRedirect } from '@/utils/authRedirect'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Form, FormField, FormItem, FormControl } from '@/components/ui/form'
import ShineBorder from '@/components/ui/ShineBorder.vue'
import TermsAndConditionsLinks from '@/components/common/TermsAndConditionsLinks.vue'
import LogoMainLink from '@/components/common/LogoMainLink.vue'
import SocialLoginButtons from '@/components/auth/SocialLoginButtons.vue'

interface Credentials {
  email: string
  password: string
}

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()

const credentials = ref<Credentials>({
  email: '',
  password: ''
})

const redirectAfterAuth = () => {
  const target = consumePostLoginRedirect(route.query.redirect as string)
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  router.push(target ?? '/')
}

onMounted(async () => {
  if (await authStore.checkAuth()) {
    redirectAfterAuth()
  }
})

const login = async () => {
  try {
    await authStore.login(credentials.value)

    const userStore = useUserStore()
    if (!userStore.currentUser) {
      try {
        await userStore.fetchCurrentUser()
      } catch (err) {
        console.warn('Failed to fetch user data after login, but proceeding with redirect:', err)
      }
    }

    await new Promise(resolve => setTimeout(resolve, 100))
    redirectAfterAuth()
  } catch (error: any) {
    console.error('Login error:', error)
  }
}
</script>

<style scoped>
.shine-text {
  color: transparent;
  background-clip: text !important;
  animation: shine 4s linear infinite;
}

.shine-text {
  background:
    radial-gradient(circle at center, #cdbeff, transparent) -200% 50% / 200% 100% no-repeat,
    #5769ff;
}

@keyframes shine {
  0% {
    background-position: 200% 0%;
  }
  25% {
    background-position: -200% 0%;
  }
}

.appear {
  animation: appear 1.5s ease-in-out;
}

@keyframes appear {
  0% {
    opacity: 0;
    transform: scale(0.5);
  }
  100% {
    opacity: 0.3;
    transform: scale(1);
  }
}
</style>
